import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import TabNavigation from '../../components/ui/TabNavigation';
import PersonColorLegend from '../../components/ui/PersonColorLegend';
import QuickActionBar from '../../components/ui/QuickActionBar';
import TaskCard from './components/TaskCard';
import TaskFilters from './components/TaskFilters';
import TaskSummary from './components/TaskSummary';
import AddTaskModal from './components/AddTaskModal';
import SearchBar from './components/SearchBar';
import LongTermTaskExportControls from './components/LongTermTaskExportControls';
import { generateRecurringInstances, hasRecurringSchedule } from '../../utils/recurringUtils';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { getUpcomingReminders, shouldShowReminder } from '../../utils/notificationUtils';
import ReminderIndicator from '../family-calendar-view/components/ReminderIndicator';
import { checkAchievements } from '../../utils/achievementUtils';
import AchievementUnlockModal from '../../components/ui/AchievementUnlockModal';
import AchievementNotification from '../../components/ui/AchievementNotification';
import { isSessionUnlocked } from '../../utils/parentalLockService';
import CalendarGrid from '../family-calendar-view/components/CalendarGrid';
import CalendarHeader from '../family-calendar-view/components/CalendarHeader';

const LongTermTaskPlanning = () => {
  const [familyMembers] = useState([
    {
      id: 1,
      name: 'Sarah',
      role: 'Mom',
      color: '#EF4444',
      initials: 'SM'
    },
    {
      id: 2,
      name: 'Michael',
      role: 'Dad',
      color: '#3B82F6',
      initials: 'MD'
    },
    {
      id: 3,
      name: 'Emma',
      role: 'Daughter',
      color: '#EC4899',
      initials: 'ED'
    },
    {
      id: 4,
      name: 'Lucas',
      role: 'Son',
      color: '#10B981',
      initials: 'LS'
    },
    {
      id: 5,
      name: 'Olivia',
      role: 'Daughter',
      color: '#F59E0B',
      initials: 'OD'
    },
    {
      id: 6,
      name: 'Noah',
      role: 'Son',
      color: '#8B5CF6',
      initials: 'NS'
    }
  ]);

  const [tasks, setTasks] = useState([]);

  const [filters, setFilters] = useState({
    person: 'all',
    status: 'all',
    priority: 'all',
    sortBy: 'dueDate'
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [activeReminders, setActiveReminders] = useState([]);
  const [dismissedReminders, setDismissedReminders] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [memberAchievements, setMemberAchievements] = useState({});
  const [achievementToShow, setAchievementToShow] = useState(null);
  const [achievementNotifications, setAchievementNotifications] = useState([]);
  const [layoutMode, setLayoutMode] = useState(() => {
    const saved = localStorage?.getItem('taskPlanningLayout');
    return saved || 'default';
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarActivities, setCalendarActivities] = useState([]);

  // Mock notification settings
  const notificationSettings = [
    { memberId: 1, enabled: true, frequency: ['1day', '1week'], deliveryTiming: 'morning', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 2, enabled: true, frequency: ['1day', '1week'], deliveryTiming: 'morning', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 3, enabled: true, frequency: ['2days', '1week'], deliveryTiming: 'evening', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 4, enabled: true, frequency: ['1day'], deliveryTiming: 'smart', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 5, enabled: true, frequency: ['1day', '1week'], deliveryTiming: 'morning', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true },
    { memberId: 6, enabled: true, frequency: ['1day'], deliveryTiming: 'smart', quietHoursStart: '22:00', quietHoursEnd: '07:00', notifyForRecurring: true }
  ];

  // Load achievements from localStorage on mount
  useEffect(() => {
    const savedAchievements = localStorage?.getItem('memberAchievements');
    if (savedAchievements) {
      try {
        setMemberAchievements(JSON?.parse(savedAchievements));
      } catch (e) {
        console?.error('Failed to load achievements:', e);
      }
    }
  }, []);

  // Save achievements to localStorage whenever they change
  useEffect(() => {
    if (Object?.keys(memberAchievements)?.length > 0) {
      localStorage?.setItem('memberAchievements', JSON?.stringify(memberAchievements));
    }
  }, [memberAchievements]);

  // Save layout preference
  useEffect(() => {
    localStorage?.setItem('taskPlanningLayout', layoutMode);
  }, [layoutMode]);

  // Convert tasks to calendar activities for calendar view
  useEffect(() => {
    const activities = tasks?.map(task => {
      const member = familyMembers?.find(m => m?.name === task?.assignedTo);
      return {
        id: task?.id,
        title: task?.title,
        date: task?.dueDate,
        time: '09:00',
        personId: member?.id,
        personName: member?.name,
        personColor: member?.color,
        personInitials: member?.initials,
        category: task?.category?.toLowerCase() || 'personal',
        description: task?.description,
        priority: task?.priority,
        status: task?.status
      };
    });
    setCalendarActivities(activities);
  }, [tasks, familyMembers]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      person: 'all',
      status: 'all',
      priority: 'all',
      sortBy: 'dueDate'
    });
    setSearchQuery('');
  };

  const handleAddTask = () => {
    setEditingTask(null);
    setIsModalOpen(true);
  };

  const handleEditTask = (task) => {
    if (!isSessionUnlocked()) {
      alert('Please unlock parental lock to edit tasks');
      return;
    }
    setEditingTask(task);
    setIsModalOpen(true);
  };

  const handleDeleteTask = (taskId) => {
    if (!isSessionUnlocked()) {
      alert('Please unlock parental lock to delete tasks');
      return;
    }
    setTasks(tasks?.filter(task => task?.id !== taskId));
  };

  const handleToggleComplete = (taskId) => {
    setTasks(prev => prev?.map(task => {
      if (task?.id === taskId) {
        const newStatus = task?.status === 'completed' ? 'in-progress' : 'completed';
        const newProgress = newStatus === 'completed' ? 100 : task?.progress;
        return { ...task, status: newStatus, progress: newProgress };
      }
      return task;
    }));
  };

  const handleStatusChange = (taskId, newStatus) => {
    setTasks(prev => {
      const updatedTasks = prev?.map(task => {
        if (task?.id === taskId) {
          const isCompleting = newStatus === 'completed' && task?.status !== 'completed';
          return {
            ...task,
            status: newStatus,
            completed: newStatus === 'completed',
            completedAt: isCompleting ? new Date()?.toISOString() : task?.completedAt
          };
        }
        return task;
      });

      // Check for achievements if task was completed
      const task = prev?.find(t => t?.id === taskId);
      if (newStatus === 'completed' && task?.status !== 'completed') {
        const memberId = familyMembers?.find(m => m?.name === task?.assignedTo)?.id;
        if (memberId) {
          const previousAchievements = memberAchievements?.[memberId] || [];
          const { newlyUnlocked, allUnlocked } = checkAchievements(
            updatedTasks,
            memberId,
            previousAchievements
          );

          if (newlyUnlocked?.length > 0) {
            // Update achievements state
            setMemberAchievements(prev => ({
              ...prev,
              [memberId]: allUnlocked
            }));

            // Show modal for first achievement
            const member = familyMembers?.find(m => m?.id === memberId);
            setAchievementToShow({
              achievement: newlyUnlocked?.[0],
              memberName: member?.name,
              memberColor: member?.color
            });

            // Queue notifications for remaining achievements
            if (newlyUnlocked?.length > 1) {
              setAchievementNotifications(prev => [
                ...prev,
                ...newlyUnlocked?.slice(1)?.map(ach => ({
                  achievement: ach,
                  memberName: member?.name,
                  memberColor: member?.color,
                  id: `${ach?.id}-${Date?.now()}`
                }))
              ]);
            }
          }
        }
      }

      return updatedTasks;
    });
  };

  const handleSaveTask = (taskData) => {
    if (editingTask) {
      setTasks(tasks?.map(task => task?.id === editingTask?.id ? { ...taskData, id: editingTask?.id } : task));
      setEditingTask(null);
    } else {
      const newTask = {
        ...taskData,
        id: Date.now()
      };
      setTasks([...tasks, newTask]);
    }
    setIsModalOpen(false);
  };

  const handleQuickAction = (action) => {
    if (action?.id === 'add-project' || action?.id === 'add-goal') {
      handleAddTask();
    }
  };

  const filteredTasks = useMemo(() => {
    return tasks?.filter(task => {
      const matchesPerson = filters?.person === 'all' || task?.assignedTo === filters?.person;
      const matchesCategory = filters?.category === 'all' || task?.category === filters?.category;
      const matchesStatus = filters?.status === 'all' || task?.status === filters?.status;
      const matchesPriority = filters?.priority === 'all' || task?.priority === filters?.priority;
      const matchesSearch = !searchQuery || 
        task?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        task?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
      
      return matchesPerson && matchesCategory && matchesStatus && matchesPriority && matchesSearch;
    });
  }, [tasks, filters?.person, filters?.category, filters?.status, filters?.priority, searchQuery]);

  // Check for upcoming reminders
  useEffect(() => {
    const checkReminders = () => {
      // Convert tasks to have date field for reminder calculation
      const tasksWithDate = tasks?.map(t => ({ ...t, date: t?.dueDate }));
      const upcomingReminders = getUpcomingReminders(tasksWithDate, notificationSettings);
      const activeOnes = upcomingReminders?.filter(r => 
        shouldShowReminder(r) && !dismissedReminders?.includes(r?.id)
      );
      setActiveReminders(activeOnes);
    };

    checkReminders();
    const interval = setInterval(checkReminders, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [tasks, notificationSettings, dismissedReminders]);

  const handleDismissReminder = (reminderId) => {
    setDismissedReminders(prev => [...prev, reminderId]);
  };

  const filteredAndSortedTasks = useMemo(() => {
    let filtered = tasks;

    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(task =>
        task?.title?.toLowerCase()?.includes(query) ||
        task?.description?.toLowerCase()?.includes(query) ||
        task?.assignedTo?.toLowerCase()?.includes(query) ||
        task?.category?.toLowerCase()?.includes(query)
      );
    }

    if (filters?.person !== 'all') {
      const person = familyMembers?.find(m => m?.id?.toString() === filters?.person);
      if (person) {
        filtered = filtered?.filter(task => task?.assignedTo === person?.name);
      }
    }

    if (filters?.status !== 'all') {
      filtered = filtered?.filter(task => task?.status === filters?.status);
    }

    if (filters?.priority !== 'all') {
      filtered = filtered?.filter(task => task?.priority === filters?.priority);
    }

    const sorted = [...filtered]?.sort((a, b) => {
      switch (filters?.sortBy) {
        case 'dueDate':
          return new Date(a.dueDate) - new Date(b.dueDate);
        case 'priority':
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          return priorityOrder?.[a?.priority] - priorityOrder?.[b?.priority];
        case 'progress':
          return b?.progress - a?.progress;
        case 'title':
          return a?.title?.localeCompare(b?.title);
        default:
          return 0;
      }
    });

    return sorted;
  }, [tasks, filters, searchQuery, familyMembers]);

  const handleLayoutToggle = () => {
    setLayoutMode(prev => prev === 'default' ? 'side-by-side' : 'default');
  };

  const handlePreviousMonth = () => {
    const newDate = new Date(currentDate);
    newDate?.setMonth(currentDate?.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate?.setMonth(currentDate?.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (date) => {
    // Filter tasks by selected date
    const tasksOnDate = tasks?.filter(task => task?.dueDate === date);
    if (tasksOnDate?.length > 0) {
      // Scroll to first task on that date
      const firstTaskElement = document?.getElementById(`task-${tasksOnDate?.[0]?.id}`);
      firstTaskElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Expand tasks with recurring schedules
  const expandedTasks = useMemo(() => {
    const today = new Date()?.toISOString()?.split('T')?.[0];
    const futureDate = new Date();
    futureDate?.setFullYear(futureDate?.getFullYear() + 2);
    const futureDateStr = futureDate?.toISOString()?.split('T')?.[0];

    const allTasks = [];
    
    tasks?.forEach(task => {
      if (hasRecurringSchedule(task)) {
        const instances = generateRecurringInstances(
          task,
          task?.recurringConfig,
          task?.dueDate,
          futureDateStr
        );
        instances?.forEach(instance => {
          allTasks?.push({
            ...instance,
            dueDate: instance?.date
          });
        });
      } else {
        allTasks?.push(task);
      }
    });

    return allTasks;
  }, [tasks]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Long-Term Planning - Starling Schedule</title>
      </Helmet>

      {/* Achievement Unlock Modal */}
      {achievementToShow && (
        <AchievementUnlockModal
          achievement={achievementToShow?.achievement}
          memberName={achievementToShow?.memberName}
          memberColor={achievementToShow?.memberColor}
          onClose={() => setAchievementToShow(null)}
        />
      )}

      {/* Achievement Notifications */}
      {achievementNotifications?.map((notification, index) => (
        <div
          key={notification?.id}
          style={{ top: `${4 + index * 6}rem` }}
        >
          <AchievementNotification
            achievement={notification?.achievement}
            memberName={notification?.memberName}
            memberColor={notification?.memberColor}
            onClose={() => {
              setAchievementNotifications(prev => 
                prev?.filter(n => n?.id !== notification?.id)
              );
            }}
          />
        </div>
      ))}

      <TabNavigation />

      <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-h1 font-heading text-foreground mb-2">
              Long-Term Task Planning
            </h1>
            <p className="text-muted-foreground">
              Plan and track family goals and projects over time
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant={layoutMode === 'side-by-side' ? 'default' : 'outline'}
              onClick={handleLayoutToggle}
              iconName={layoutMode === 'side-by-side' ? 'layout-grid' : 'columns'}
              iconPosition="left"
              className="whitespace-nowrap"
            >
              {layoutMode === 'side-by-side' ? 'Side by Side' : 'Default'}
            </Button>
            <LongTermTaskExportControls 
              tasks={filteredAndSortedTasks} 
              familyMembers={familyMembers}
            />
          </div>
        </div>

        {layoutMode === 'default' ? (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="lg:col-span-3 space-y-4 md:space-y-6">
              <SearchBar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />

              <TaskFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                familyMembers={familyMembers}
              />

              <TaskSummary tasks={tasks} familyMembers={familyMembers} />

              <div className="space-y-4">
                {filteredAndSortedTasks?.length === 0 ? (
                  <div className="bg-card border border-border rounded-xl p-8 md:p-12 text-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <Icon name="inbox" size={32} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
                      No tasks found
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground mb-6">
                      {searchQuery || filters?.person !== 'all' || filters?.status !== 'all' || filters?.priority !== 'all' ?'Try adjusting your filters or search query' :'Get started by creating your first long-term task'}
                    </p>
                    <Button variant="default" onClick={handleAddTask} iconName="plus" iconPosition="left">
                      Add Long Term Task
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm md:text-base text-muted-foreground">
                        Showing {filteredAndSortedTasks?.length} of {tasks?.length} tasks
                      </p>
                    </div>
                    {filteredAndSortedTasks?.map(task => (
                      <TaskCard
                        key={task?.id}
                        task={task}
                        onEdit={handleEditTask}
                        onDelete={handleDeleteTask}
                        onToggleComplete={handleToggleComplete}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <PersonColorLegend
                  layout="vertical"
                  familyMembers={familyMembers}
                  onPersonUpdate={() => {}}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6 md:mb-8">
            {/* Calendar Panel */}
            <div className="bg-card border border-border rounded-xl p-4 md:p-6">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-foreground mb-2">Task Calendar</h2>
                <p className="text-sm text-muted-foreground">View tasks by due date</p>
              </div>
              <CalendarHeader
                currentDate={currentDate}
                viewMode="month"
                onViewModeChange={() => {}}
                onPreviousPeriod={handlePreviousMonth}
                onNextPeriod={handleNextMonth}
                onToday={handleToday}
                hideViewToggle={true}
              />
              <div className="mt-4">
                <CalendarGrid
                  currentDate={currentDate}
                  activities={calendarActivities}
                  onActivityClick={(activity) => {
                    const task = tasks?.find(t => t?.id === activity?.id);
                    if (task) handleEditTask(task);
                  }}
                  onDateClick={handleDateClick}
                  selectedPersonFilters={[]}
                  onDragStart={() => {}}
                  onDragOver={() => {}}
                  onDrop={() => {}}
                  preferences={{}}
                />
              </div>
              <div className="mt-4">
                <PersonColorLegend
                  layout="horizontal"
                  familyMembers={familyMembers}
                  onPersonUpdate={() => {}}
                />
              </div>
            </div>

            {/* Task List Panel */}
            <div className="space-y-4">
              <div className="bg-card border border-border rounded-xl p-4 md:p-6">
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-foreground mb-2">Task List</h2>
                  <p className="text-sm text-muted-foreground">Manage and track tasks</p>
                </div>
                <SearchBar
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                />
              </div>

              <TaskFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                familyMembers={familyMembers}
              />

              <TaskSummary tasks={tasks} familyMembers={familyMembers} />

              <div className="space-y-4 max-h-[800px] overflow-y-auto pr-2">
                {filteredAndSortedTasks?.length === 0 ? (
                  <div className="bg-card border border-border rounded-xl p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <Icon name="inbox" size={32} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      No tasks found
                    </h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      {searchQuery || filters?.person !== 'all' || filters?.status !== 'all' || filters?.priority !== 'all' ? 'Try adjusting your filters or search query' : 'Get started by creating your first long-term task'}
                    </p>
                    <Button variant="default" onClick={handleAddTask} iconName="plus" iconPosition="left">
                      Add Long Term Task
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-muted-foreground">
                        Showing {filteredAndSortedTasks?.length} of {tasks?.length} tasks
                      </p>
                    </div>
                    {filteredAndSortedTasks?.map(task => (
                      <div key={task?.id} id={`task-${task?.id}`}>
                        <TaskCard
                          task={task}
                          onEdit={handleEditTask}
                          onDelete={handleDeleteTask}
                          onToggleComplete={handleToggleComplete}
                        />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      <QuickActionBar
        context="longterm"
        onActionSelect={handleQuickAction}
      />

      <AddTaskModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveTask}
        editTask={editingTask}
        familyMembers={familyMembers}
      />

      <ReminderIndicator 
        reminders={activeReminders}
        onDismiss={handleDismissReminder}
      />
    </div>
  );
};

export default LongTermTaskPlanning;